import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import { axiosInstance } from '../../services/apiInterceptor';

interface BlobDownloadOptions {
  url: string;
  method?: 'GET' | 'POST';
  data?: any;
  defaultFileName?: string;
  successMessage?: string;
}

interface UseBlobDownloadReturn {
  downloadBlob: (options: BlobDownloadOptions) => Promise<void>;
  isLoading: boolean;
}

function parseFileNameFromHeaders(
  headers: Record<string, any>,
  fallback: string
): string {
  const contentDisposition = headers['content-disposition'];
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+?)"?$/);
    if (match && match.length >= 2) return match[1];
  }
  return fallback;
}

async function extractBlobErrorMessage(error: any): Promise<string> {
  try {
    const blob = error?.response?.data;
    if (blob instanceof Blob) {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return parsed?.error?.message || 'Request failed.';
    }
    return error?.response?.data?.error?.message || 'Request failed.';
  } catch {
    return 'Request failed.';
  }
}

export default function useBlobDownload(): UseBlobDownloadReturn {
  const [isLoading, setIsLoading] = useState(false);

  const downloadBlob = useCallback(async (options: BlobDownloadOptions) => {
    const {
      url,
      method = 'POST',
      data = null,
      defaultFileName = 'download',
      successMessage = 'File downloaded successfully.',
    } = options;

    setIsLoading(true);
    try {
      const response = await axiosInstance.request({
        url,
        method,
        data,
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute(
        'download',
        parseFileNameFromHeaders(response.headers, defaultFileName)
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success(<ToastComponent label='Success' message={successMessage} />);
    } catch (err: any) {
      const message = await extractBlobErrorMessage(err);
      toast.error(<ToastComponent label='Error' message={message} />, {
        autoClose: 30000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { downloadBlob, isLoading };
}
